'use client'

import { FILTER } from '@/app/constants'
import dynamic from 'next/dynamic'
import { FC, useCallback, useState } from 'react'
import { DestPagesProps, FiltersCountMap, SORT_BY } from '../types'
import HotelListSorting from './HotelListSorting'

export interface Props {
	pageProps: DestPagesProps
	defaultStars?: number
	availableRates: { min: number; max: number }
	isFallbackData?: boolean
	isSingleHotels?: boolean
	isSimilarHotels?: boolean
	hasFilters?: boolean
	availableFilters: FiltersCountMap
}

const HotelListFilters = dynamic(() => import('./HotelListFilters'), {
	ssr: false,
})

const SortingAndFilters: FC<Props> = ({
	pageProps,
	availableRates,
	isFallbackData,
	isSingleHotels,
	hasFilters,
	availableFilters,
	isSimilarHotels,
}) => {
	const [openedFilter, setOpenedFilter] = useState<string | null>(null)

	const setFilterHash = useCallback((filter: string) => {
		if (typeof window !== 'undefined') {
			window.location.hash = filter
		}
	}, [])

	const removeFilterHash = useCallback(() => {
		if (typeof window !== 'undefined') {
			try {
				window.history.replaceState(
					null,
					document.title,
					window.location.pathname + window.location.search
				)
			} catch (error) {
				console.error('Error removing hash:', error)
			}
		}
	}, [])

	const handleOpenFilter = useCallback(
		(filterToOpen: string | null) => {
			setOpenedFilter(filterToOpen)
			if (filterToOpen) {
				setFilterHash(filterToOpen)
			} else {
				removeFilterHash()
			}
		},
		[setFilterHash, removeFilterHash]
	)

	const handleHashChange = useCallback((hash: string | null) => {
		if (hash) {
			window.history.replaceState(null, '', hash)
		} else {
			window.history.replaceState(
				null,
				'',
				window.location.pathname + window.location.search
			)
		}
	}, [])

	return (
		<div className="flex max-md:mb-2">
			{!isFallbackData && !isSingleHotels && (
				<HotelListFilters
					pageProps={pageProps}
					onOpen={() => handleOpenFilter(FILTER.FILTERS)}
					onClose={() => handleOpenFilter(null)}
					onHashChange={handleHashChange}
					availableRates={availableRates}
					availableFilters={availableFilters}
				/>
			)}
			{!hasFilters && (
				<HotelListSorting
					pageProps={pageProps}
					defaultValueBy={SORT_BY.POPULARITY}
					onOpen={() => handleOpenFilter(FILTER.SORTING)}
					onClose={() => handleOpenFilter(null)}
					onHashChange={handleHashChange}
				/>
			)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(SortingAndFilters), { ssr: false })
